import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import {
    marketingBlogEntity, marketingContentStatusEntity, marketingContentTypeEntity,
    marketingNewsletterEntity, marketingSocialPostEntity
} from 'mj_generatedentities';

type ContentItemType = 'blog' | 'social-post' | 'newsletter';
type ContentTabFilter = 'all' | 'blog' | 'social-post' | 'newsletter';

interface ContentItem {
    id: string;
    type: ContentItemType;
    title: string;
    icon: string;
    statusLabel: string;
    statusClass: string;
    platformName: string | null;
    pageViews: number | null;
    likes: number | null;
    socialShares: number | null;
    createdAt: Date;
}

@Component({
    standalone: false,
    selector: 'mkt-content-library',
    template: `
        <div class="cl-root">
            <!-- Filter Bar -->
            <div class="cl-filters">
                <div class="cl-tabs">
                    <button class="cl-tab" [class.cl-tab--active]="activeTab === 'all'" (click)="setTab('all')">
                        All <span class="cl-tab__count">{{ allItems.length }}</span>
                    </button>
                    <button class="cl-tab" [class.cl-tab--active]="activeTab === 'blog'" (click)="setTab('blog')">
                        <i class="fa-solid fa-file-lines"></i> Blogs <span class="cl-tab__count">{{ blogs.length }}</span>
                    </button>
                    <button class="cl-tab" [class.cl-tab--active]="activeTab === 'social-post'" (click)="setTab('social-post')">
                        <i class="fa-solid fa-share-nodes"></i> Social Posts <span class="cl-tab__count">{{ socialPosts.length }}</span>
                    </button>
                    <button class="cl-tab" [class.cl-tab--active]="activeTab === 'newsletter'" (click)="setTab('newsletter')">
                        <i class="fa-solid fa-envelope"></i> Newsletters <span class="cl-tab__count">{{ newsletters.length }}</span>
                    </button>
                </div>
                <div class="cl-filters__right">
                    <select class="cl-select" [(ngModel)]="statusFilter" (ngModelChange)="applyFilters()">
                        <option value="all">All Statuses</option>
                        <option value="Draft">Draft</option>
                        <option value="Approved">Approved</option>
                        <option value="Published">Published</option>
                    </select>
                    <div class="cl-search">
                        <i class="fa-solid fa-magnifying-glass cl-search__icon"></i>
                        <input class="cl-search__input"
                               type="text"
                               placeholder="Search by title..."
                               [(ngModel)]="searchQuery"
                               (ngModelChange)="applyFilters()">
                    </div>
                </div>
            </div>

            <!-- Card Grid -->
            <div class="cl-grid" *ngIf="filteredItems.length > 0">
                <div *ngFor="let item of filteredItems; trackBy: trackById"
                     class="cl-card"
                     (click)="onCardClick(item)">
                    <div class="cl-card__header">
                        <i class="fa-solid cl-card__icon" [ngClass]="item.icon"></i>
                        <span class="cl-card__type-badge" [ngClass]="'cl-card__type-badge--' + item.type">
                            {{ item.type === 'blog' ? 'Blog' : item.type === 'social-post' ? 'Social' : 'Newsletter' }}
                        </span>
                    </div>
                    <h4 class="cl-card__title">{{ item.title }}</h4>
                    <div class="cl-card__meta">
                        <span class="cl-card__status" [ngClass]="item.statusClass">{{ item.statusLabel }}</span>
                        <span class="cl-card__platform" *ngIf="item.platformName">
                            <i class="fa-solid fa-globe"></i> {{ item.platformName }}
                        </span>
                    </div>
                    <div class="cl-card__metrics" *ngIf="item.pageViews != null || item.likes != null">
                        <span class="cl-card__metric" *ngIf="item.pageViews != null">
                            <i class="fa-solid fa-eye"></i> {{ item.pageViews | number }}
                        </span>
                        <span class="cl-card__metric" *ngIf="item.likes != null">
                            <i class="fa-solid fa-thumbs-up"></i> {{ item.likes | number }}
                        </span>
                        <span class="cl-card__metric" *ngIf="item.socialShares != null">
                            <i class="fa-solid fa-share"></i> {{ item.socialShares | number }}
                        </span>
                    </div>
                    <div class="cl-card__footer">
                        <span class="cl-card__date">
                            <i class="fa-regular fa-calendar"></i>
                            {{ item.createdAt | date:'mediumDate' }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div class="cl-empty" *ngIf="filteredItems.length === 0">
                <i class="fa-solid fa-folder-open"></i>
                <p>No content matches your filters.</p>
            </div>
        </div>
    `,
    styles: [`
        :host {
            display: block;
            height: 100%;
            overflow: hidden;
        }

        .cl-root {
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
        }

        /* ---- Filter Bar ---- */
        .cl-filters {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 12px 16px;
            border-bottom: 1px solid var(--mj-border-default);
            background: var(--mj-bg-surface);
            flex-shrink: 0;
            flex-wrap: wrap;
        }

        .cl-tabs {
            display: flex;
            gap: 4px;
        }

        .cl-tab {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 14px;
            border: 1px solid var(--mj-border-default);
            border-radius: 20px;
            background: var(--mj-bg-surface);
            color: var(--mj-text-secondary);
            font-size: 0.8rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.15s;
            white-space: nowrap;
        }
        .cl-tab:hover {
            background: var(--mj-bg-page);
            border-color: var(--mj-brand-primary);
        }
        .cl-tab--active {
            background: var(--mj-brand-primary);
            color: var(--mj-text-inverse);
            border-color: var(--mj-brand-primary);
        }
        .cl-tab--active:hover {
            background: var(--mj-brand-primary);
        }
        .cl-tab__count {
            font-size: 0.7rem;
            opacity: 0.75;
        }

        .cl-filters__right {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .cl-select {
            padding: 6px 10px;
            border: 1px solid var(--mj-border-default);
            border-radius: 6px;
            background: var(--mj-bg-surface);
            color: var(--mj-text-primary);
            font-size: 0.8rem;
            cursor: pointer;
            outline: none;
            transition: border-color 0.15s;
        }
        .cl-select:focus {
            border-color: var(--mj-brand-primary);
        }

        .cl-search {
            position: relative;
            display: flex;
            align-items: center;
        }
        .cl-search__icon {
            position: absolute;
            left: 10px;
            font-size: 0.75rem;
            color: var(--mj-text-secondary);
            pointer-events: none;
        }
        .cl-search__input {
            padding: 6px 10px 6px 30px;
            border: 1px solid var(--mj-border-default);
            border-radius: 6px;
            background: var(--mj-bg-surface);
            color: var(--mj-text-primary);
            font-size: 0.8rem;
            width: 200px;
            outline: none;
            transition: border-color 0.15s;
        }
        .cl-search__input::placeholder {
            color: var(--mj-text-secondary);
        }
        .cl-search__input:focus {
            border-color: var(--mj-brand-primary);
        }

        /* ---- Card Grid ---- */
        .cl-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 16px;
            padding: 16px;
            overflow-y: auto;
            flex: 1;
        }

        .cl-card {
            background: var(--mj-bg-surface);
            border: 1px solid var(--mj-border-default);
            border-radius: 8px;
            padding: 16px;
            cursor: pointer;
            transition: box-shadow 0.2s ease, border-color 0.2s ease;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .cl-card:hover {
            box-shadow: var(--mj-shadow-sm);
            border-color: var(--mj-brand-primary);
        }

        .cl-card__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        .cl-card__icon {
            font-size: 1rem;
            color: var(--mj-text-secondary);
        }
        .cl-card__type-badge {
            font-size: 0.65rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            padding: 2px 8px;
            border-radius: 10px;
        }
        .cl-card__type-badge--blog {
            background: color-mix(in srgb, var(--mj-brand-primary) 12%, transparent);
            color: var(--mj-brand-primary);
        }
        .cl-card__type-badge--social-post {
            background: color-mix(in srgb, var(--mj-status-info-bg) 100%, transparent);
            color: var(--mj-status-info-text);
        }
        .cl-card__type-badge--newsletter {
            background: color-mix(in srgb, var(--mj-status-success-bg) 100%, transparent);
            color: var(--mj-status-success-text);
        }

        .cl-card__title {
            margin: 0;
            font-size: 0.9rem;
            font-weight: 600;
            color: var(--mj-text-primary);
            line-height: 1.35;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .cl-card__meta {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
        }
        .cl-card__status {
            display: inline-block;
            padding: 2px 10px;
            border-radius: 12px;
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.03em;
        }
        .cl-card__status--draft {
            background: var(--mj-status-warning-bg);
            color: var(--mj-status-warning-text);
        }
        .cl-card__status--approved,
        .cl-card__status--ready {
            background: var(--mj-status-success-bg);
            color: var(--mj-status-success-text);
        }
        .cl-card__status--published {
            background: var(--mj-status-info-bg);
            color: var(--mj-status-info-text);
        }
        .cl-card__status--default {
            background: var(--mj-bg-page);
            color: var(--mj-text-secondary);
        }

        .cl-card__platform {
            font-size: 0.75rem;
            color: var(--mj-text-secondary);
        }
        .cl-card__platform i {
            margin-right: 3px;
        }

        .cl-card__metrics {
            display: flex;
            gap: 12px;
            padding-top: 4px;
        }
        .cl-card__metric {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 0.75rem;
            color: var(--mj-text-secondary);
        }
        .cl-card__metric i {
            font-size: 0.7rem;
        }

        .cl-card__footer {
            margin-top: auto;
            padding-top: 4px;
        }
        .cl-card__date {
            font-size: 0.7rem;
            color: var(--mj-text-secondary);
        }
        .cl-card__date i {
            margin-right: 4px;
        }

        /* ---- Empty State ---- */
        .cl-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 80px 20px;
            color: var(--mj-text-secondary);
            flex: 1;
        }
        .cl-empty i {
            font-size: 2.5rem;
            margin-bottom: 12px;
            opacity: 0.5;
        }
        .cl-empty p {
            font-size: 0.9rem;
            margin: 0;
        }
    `]
})
export class ContentLibraryComponent implements OnChanges {
    @Input() blogs: marketingBlogEntity[] = [];
    @Input() socialPosts: marketingSocialPostEntity[] = [];
    @Input() newsletters: marketingNewsletterEntity[] = [];
    @Input() contentStatuses: marketingContentStatusEntity[] = [];
    @Input() contentTypes: marketingContentTypeEntity[] = [];
    @Input() socialPlatforms: { ID: string; Name: string }[] = [];
    @Input() metrics: { ContentID: string; PageViews: number | null; Likes: number | null; SocialShares: number | null }[] = [];

    @Output() openContent = new EventEmitter<{ type: ContentItemType; id: string }>();

    activeTab: ContentTabFilter = 'all';
    statusFilter = 'all';
    searchQuery = '';

    allItems: ContentItem[] = [];
    filteredItems: ContentItem[] = [];

    ngOnChanges(_changes: SimpleChanges): void {
        this.allItems = this.normalizeAll();
        this.applyFilters();
    }

    setTab(tab: ContentTabFilter): void {
        this.activeTab = tab;
        this.applyFilters();
    }

    applyFilters(): void {
        this.filteredItems = this.allItems
            .filter(item => this.activeTab === 'all' || item.type === this.activeTab)
            .filter(item => this.statusFilter === 'all' || item.statusLabel.toLowerCase() === this.statusFilter.toLowerCase())
            .filter(item => {
                if (!this.searchQuery.trim()) return true;
                return item.title.toLowerCase().includes(this.searchQuery.trim().toLowerCase());
            });
    }

    onCardClick(item: ContentItem): void {
        this.openContent.emit({ type: item.type, id: item.id });
    }

    trackById(_index: number, item: ContentItem): string {
        return item.id;
    }

    // ─── Pure normalization ───────────────────────────

    private normalizeAll(): ContentItem[] {
        const blogItems = this.blogs.map(b => this.normalizeBlog(b));
        const socialItems = this.socialPosts.map(sp => this.normalizeSocialPost(sp));
        const newsletterItems = this.newsletters.map(nl => this.normalizeNewsletter(nl));
        return [...blogItems, ...socialItems, ...newsletterItems]
            .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }

    private normalizeBlog(blog: marketingBlogEntity): ContentItem {
        const statusLabel = blog.Status || 'Draft';
        const m = this.findMetrics(blog.ID);
        return {
            id: blog.ID,
            type: 'blog',
            title: blog.Title,
            icon: 'fa-file-lines',
            statusLabel,
            statusClass: this.statusToCssClass(statusLabel),
            platformName: null,
            pageViews: m?.PageViews ?? null,
            likes: m?.Likes ?? null,
            socialShares: m?.SocialShares ?? null,
            createdAt: new Date(blog.__mj_CreatedAt),
        };
    }

    private normalizeSocialPost(post: marketingSocialPostEntity): ContentItem {
        const statusLabel = post.ContentStatus || 'Draft';
        const platformName = post.SocialPlatform || this.lookupPlatformName(post.SocialPlatformID);
        const m = this.findMetrics(post.ID);
        return {
            id: post.ID,
            type: 'social-post',
            title: post.Body.length > 80 ? post.Body.substring(0, 80) + '...' : post.Body,
            icon: 'fa-share-nodes',
            statusLabel,
            statusClass: this.statusToCssClass(statusLabel),
            platformName,
            pageViews: m?.PageViews ?? null,
            likes: m?.Likes ?? null,
            socialShares: m?.SocialShares ?? null,
            createdAt: new Date(post.__mj_CreatedAt),
        };
    }

    private normalizeNewsletter(nl: marketingNewsletterEntity): ContentItem {
        const statusLabel = nl.ContentStatus || 'Draft';
        const m = this.findMetrics(nl.ID);
        return {
            id: nl.ID,
            type: 'newsletter',
            title: nl.Subject,
            icon: 'fa-envelope',
            statusLabel,
            statusClass: this.statusToCssClass(statusLabel),
            platformName: null,
            pageViews: m?.PageViews ?? null,
            likes: m?.Likes ?? null,
            socialShares: m?.SocialShares ?? null,
            createdAt: new Date(nl.__mj_CreatedAt),
        };
    }

    // ─── Helpers ──────────────────────────────────────

    private findMetrics(contentId: string): { PageViews: number | null; Likes: number | null; SocialShares: number | null } | undefined {
        return this.metrics.find(m => m.ContentID === contentId);
    }

    private lookupPlatformName(platformId: string): string | null {
        const p = this.socialPlatforms.find(sp => sp.ID === platformId);
        return p ? p.Name : null;
    }

    private statusToCssClass(status: string): string {
        const key = status.toLowerCase();
        switch (key) {
            case 'draft': return 'cl-card__status--draft';
            case 'approved':
            case 'ready': return 'cl-card__status--approved';
            case 'published': return 'cl-card__status--published';
            default: return 'cl-card__status--default';
        }
    }
}
